import { gql } from "@apollo/client";

export const FIND_ALL_FRAUDS_QUERY = gql`
    query findAllFrauds {
        findAllFrauds {
            state
            bold
            text
            aboveSignature
            fontSize
        }
    }
`;

export const FIND_MEMBERS_QUERY = gql`
    query findMembers($effectiveDate: Date) {
        findMembers(effectiveDate: $effectiveDate) {
            id
            policyId
            firstName
            lastName
            dateOfBirth
        }
    }
`;

const memberFields = `
    id
    policyId
    firstName
    middleName
    lastName
    dateOfBirth
    gender
    dependents {
        dateOfBirth
        id
        firstName
        lastName
        middleName
        coverages {
            groupTermLife {
                name
            }
            voluntaryTermLife {
                name
            }
        }
    }
    homePhoneNumber
    residentialAddress {
        lineOne
        city
        stateAbbreviation
        zip
    }
    emailAddress
    language
    employmentInformation {
        dateOfHire
        hoursWorked
        billGroupId
        billGroupName
        employerName
        memberGroupName
    }
    ssn
    underwritingInformation {
        wage
        wageFrequency
        wageEffectiveDate
    }
    coverages {
      groupTermLife {
        name
      }
      longTermDisability {
        name
      }
      shortTermDisability {
        name
      }
      voluntaryTermLife {
        name
      }
    }`

export const FIND_MEMBER_QUERY = gql`
    query findMember($policyId: String!, $memberId: String!, $effectiveDate: Date) {
        findMember(policyId: $policyId, memberId: $memberId, effectiveDate: $effectiveDate) {
            ${memberFields}
        }
    }
`;

export const FIND_OWN_MEMBER_QUERY = gql`
    query findOwnMember {
        findOwnMember {
            ${memberFields}
        }
    }
`;

export const VALIDATE_EMPLOYER_REIMBURSEMENT_QUERY = gql`
    query validateEmployerReimbursement($workState: String!) {
        validateEmployerReimbursement(workState: $workState)
    }
`;

export const VALIDATE_PAID_FAMILY_AND_MEDICAL_LEAVE_SUPPORTED_QUERY = gql`
    query validatePaidFamilyAndMedicalLeaveSupported($workState: String!) {
        validatePaidFamilyAndMedicalLeaveSupported(workState: $workState)
    }
`;

export const VALIDATE_PAID_FAMILY_AND_MEDICAL_LEAVE_WORK_LOCATION = gql`
    query validatePaidFamilyAndMedicalLeaveWorkLocation($workState: String!) {
        validatePaidFamilyAndMedicalLeaveWorkLocation(workState: $workState)
    }
`;

export const VALIDATE_PFML_1250_MINIMUM_REQUIRED_HOURS = gql`
    query validatePfml1250MinimumRequiredHours($workState: String!) {
        validatePfml1250MinimumRequiredHours(workState: $workState)
    }
`;

export const FIND_INCOMPLETE_INTAKES_QUERY = gql`
    query findIncompleteIntakes {
        findIncompleteIntakes {
            benefit
            party
            encryptedFormData
        }
    }
`;

export const FIND_LEAVE_PLANS_QUERY = gql`
    query findLeavePlans($workState: String!, $policyId: String!, $effectiveDate: Date) {
        findLeavePlans(workState: $workState, policyId: $policyId, effectiveDate: $effectiveDate) {
            type
            relationship
            medical
        }
    }
`;

export const FIND_WORK_LOCATION_QUERY = gql`
    query findWorkLocation($policyId: String!, $memberId: String!) {
        findWorkLocation(policyId: $policyId, memberId: $memberId) {
            workLocation
        }
    }
`;

export const FIND_SCREENINGS_QUERY = gql`
    query findScreenings($residencyState: String!, $policyId: String) {
        findScreenings(residencyState: $residencyState, policyId: $policyId) {
            type
            excludedContractStates
        }
    }
`;

export const FIND_EMPLOYER_QUERY = gql`
    query findEmployer {
        findEmployer {
            emailAddress
            contactName
            policies {
                id
                name
                units {
                    policyId
                    id
                    name
                }
                memberGroups {
                    policyId
                    id
                    name
                }
            }
        }
    }
`;

export const FIND_MEMBER_ACTIVITY_QUERY = gql`
    query findMemberActivity($activityId: String!) {
        findMemberActivity(activityId: $activityId) {
            id
            status
            type
        }
    }
`;

export const FIND_POLICY_QUERY = gql`
    query findPolicy($policyId: String!, $memberId: String!, $effectiveDate: Date) {
        findPolicy(policyId: $policyId, memberId: $memberId, effectiveDate: $effectiveDate) {
            id
            contractState
            administrationType
            memberGroups {
                policyId
                id
                coverages {
                    accident {
                        configuration {
                            employerContributoryPercentage
                        }
                        dependent {
                            name
                        }
                    }
                     criticalIllness {
                      configuration {
                        benefitSchedule {
                          benefitsPayable {
                            type
                            isChildhoodOnlyCondition
                          }
                        }
                      }
                      spouse {
                        name
                      }
                      child {
                        name
                      }
                    }
                    groupTermLife {
                        configuration {
                            salaryChangeEffectiveDate
                        }
                        dependent {
                            name
                        }
                    }
                    hospitalIndemnityChoice {
                        coverages {
                            configuration {
                                employerContributoryPercentage
                            }
                            dependent {
                                name
                            }
                        }
                    }
                    longTermDisability {
                        isBonusUp
                        configuration {
                            salaryChangeEffectiveDate
                            definitionOfEarnings
                            employerContributoryPercentage
                        }
                    }
                    paidFamilyLeave {
                        name
                    }
                    paidMedicalLeave {
                        name
                    }
                    shortTermDisability {
                        isBonusUp
                        configuration {
                            salaryChangeEffectiveDate
                            definitionOfEarnings
                            employerContributoryPercentage
                        }
                    }
                    voluntaryTermLife {
                        configuration {
                            salaryChangeEffectiveDate
                        }
                        spouse {
                            name
                        }
                        child {
                            name
                        }
                    }
                }
            }
        }
    }
`;

export const VALIDATE_ADDRESS_QUERY = gql`
    query validateAddress($validateAddressInput: ValidateAddressInput!) {
        validateAddress(validateAddressInput: $validateAddressInput) {
            streetAddress
            city
            state
            zipCode
        }
    }
`;

export const CREATE_OR_UPDATE_INCOMPLETE_INTAKE_MUTATION = gql`
     mutation createOrUpdateIncompleteIntake($incompleteIntakeInput: IncompleteIntakeInput!) {
        createOrUpdateIncompleteIntake(incompleteIntakeInput: $incompleteIntakeInput) 
    }
`;

export const GENERATE_HIPAA_PDF_MUTATION = gql`
    mutation generateHipaaPdf($hipaaPdfInput: HipaaPdfInput!) {
        generateHipaaPdf(hipaaPdfInput: $hipaaPdfInput)
    }
`;

export const CREATE_OR_UPDATE_MEMBER_MUTATION = gql`
    mutation createOrUpdateMember($memberEnrollmentUpdateInput: MemberEnrollmentUpdateInput!) {
        createOrUpdateMember(memberEnrollmentUpdateInput: $memberEnrollmentUpdateInput) {
            id
            status
            type
        }
    }
`;

export const UPDATE_MEMBER_ADDRESS = gql`
    mutation updateMemberAddress($memberAddressInput: MemberAddressInput!) {
        updateMemberAddress(memberAddressInput: $memberAddressInput) {
            id
            status
            type
        }
    }
`;

export const INTAKE_MUTATION = gql`
    mutation intake($intakeInput: JSON!) {
        intake(intakeInput: $intakeInput) {
            claimNumber
        }
    }
`;